import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Ticket, Info, ZoomIn, ZoomOut, RotateCcw, ShoppingCart, X } from "lucide-react";

export default function DemoPage() {
  const navigate = useNavigate();
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [showMobileCart, setShowMobileCart] = useState(false);

  // Demo venue layout
  const demoLayout = {
    name: "Grand Theater",
    event: "Live Concert 2025",
    sections: [
      {
        name: "VIP",
        rows: [
          { label: "A", seats: 12, price: 150, color: "#8B5CF6" },
          { label: "B", seats: 14, price: 150, color: "#8B5CF6" },
        ]
      },
      {
        name: "Premium",
        rows: [
          { label: "C", seats: 16, price: 100, color: "#3B82F6" },
          { label: "D", seats: 18, price: 100, color: "#3B82F6" },
          { label: "E", seats: 18, price: 100, color: "#3B82F6" },
        ]
      },
      {
        name: "Standard",
        rows: [
          { label: "F", seats: 20, price: 60, color: "#10B981" },
          { label: "G", seats: 20, price: 60, color: "#10B981" },
          { label: "H", seats: 22, price: 60, color: "#10B981" },
          { label: "I", seats: 22, price: 50, color: "#10B981" },
          { label: "J", seats: 24, price: 50, color: "#10B981" },
        ]
      }
    ]
  };

  // Some seats are already booked (demo)
  const bookedSeats = ["A-3", "A-4", "B-7", "B-8", "C-10", "D-5", "D-6", "E-12", "F-1", "F-2", "G-15", "H-8", "H-9", "I-20", "J-12"];

  const toggleSeat = (seatId, price) => {
    if (bookedSeats.includes(seatId)) return;
    
    if (selectedSeats.find(s => s.id === seatId)) {
      setSelectedSeats(selectedSeats.filter(s => s.id !== seatId));
    } else {
      if (selectedSeats.length >= 8) {
        toast.error("Maximum 8 seats per booking");
        return;
      }
      setSelectedSeats([...selectedSeats, { id: seatId, price }]);
    }
  };

  const totalPrice = selectedSeats.reduce((sum, s) => sum + s.price, 0);

  const handleCheckout = () => {
    if (selectedSeats.length === 0) {
      toast.error("Please select at least one seat");
      return;
    }
    toast.success(`Demo checkout: ${selectedSeats.length} seats for £${totalPrice}`);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="px-2 sm:px-3">
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="h-6 w-px bg-gray-300 hidden sm:block" />
            <div>
              <h1 className="font-semibold text-gray-900 text-sm sm:text-base">{demoLayout.name}</h1>
              <p className="text-xs sm:text-sm text-gray-500">{demoLayout.event}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm text-violet-600 font-medium px-2 sm:px-3 py-1 bg-violet-50 rounded-full">
              Demo
            </span>
            {/* Mobile Cart Button */}
            <button 
              className="lg:hidden relative p-2 bg-violet-600 text-white rounded-full"
              onClick={() => setShowMobileCart(true)}
            >
              <ShoppingCart className="w-5 h-5" />
              {selectedSeats.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {selectedSeats.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="grid lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Seating Chart */}
          <div className="lg:col-span-3">
            <Card className="p-3 sm:p-6 overflow-hidden">
              {/* Zoom Controls & Legend */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setZoom(Math.max(0.5, zoom - 0.1))} className="h-8 w-8 p-0">
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-xs sm:text-sm text-gray-500 min-w-[40px] text-center">{Math.round(zoom * 100)}%</span>
                  <Button variant="outline" size="sm" onClick={() => setZoom(Math.min(1.5, zoom + 0.1))} className="h-8 w-8 p-0">
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setZoom(1)} className="h-8 w-8 p-0">
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm overflow-x-auto">
                  <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-gray-300" />
                    <span className="text-gray-600">Available</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-violet-600" />
                    <span className="text-gray-600">Selected</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-gray-500" />
                    <span className="text-gray-600">Booked</span>
                  </div>
                </div>
              </div>

              {/* Scrollable Seating Chart */}
              <div className="overflow-x-auto overflow-y-auto pb-4 -mx-3 sm:mx-0 px-3 sm:px-0">
                <div 
                  className="min-w-[500px]"
                  style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
                >
                  {/* Stage */}
                  <div className="flex justify-center mb-6 sm:mb-8">
                    <div className="bg-gray-800 text-white px-8 sm:px-16 py-2 sm:py-3 rounded-lg text-center">
                      <span className="font-medium text-sm sm:text-base">STAGE</span>
                    </div>
                  </div>

                  {/* Sections */}
                  <div className="space-y-6 sm:space-y-8">
                    {demoLayout.sections.map((section, sIdx) => (
                      <div key={sIdx}>
                        <div className="text-center mb-2 sm:mb-3">
                          <span className="text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 px-2 sm:px-3 py-1 rounded-full">
                            {section.name}
                          </span>
                        </div>
                        <div className="space-y-1 sm:space-y-2">
                          {section.rows.map((row, rIdx) => (
                            <div key={rIdx} className="flex items-center justify-center gap-0.5 sm:gap-1">
                              <span className="w-4 sm:w-6 text-[10px] sm:text-xs text-gray-500 text-right">{row.label}</span>
                              <div className="flex gap-0.5 sm:gap-1">
                                {Array.from({ length: row.seats }).map((_, seatIdx) => {
                                  const seatId = `${row.label}-${seatIdx + 1}`;
                                  const isBooked = bookedSeats.includes(seatId);
                                  const isSelected = selectedSeats.find(s => s.id === seatId);
                                  
                                  return (
                                    <button
                                      key={seatIdx}
                                      onClick={() => toggleSeat(seatId, row.price)}
                                      disabled={isBooked}
                                      className={`w-5 h-5 sm:w-6 sm:h-6 rounded text-[8px] sm:text-xs font-medium transition-all ${
                                        isBooked 
                                          ? 'bg-gray-500 cursor-not-allowed' 
                                          : isSelected 
                                            ? 'bg-violet-600 text-white scale-110 shadow-lg' 
                                            : 'bg-gray-300 hover:bg-gray-400 active:scale-95'
                                      }`}
                                      style={{ backgroundColor: !isBooked && !isSelected ? row.color + '40' : undefined }}
                                      title={`Seat ${seatId} - £${row.price}`}
                                    >
                                      {seatIdx + 1}
                                    </button>
                                  );
                                })}
                              </div>
                              <span className="w-4 sm:w-6 text-[10px] sm:text-xs text-gray-500">{row.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile Price Guide */}
              <div className="mt-4 pt-4 border-t lg:hidden">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Price Guide</h4>
                <div className="flex flex-wrap gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: '#8B5CF6' }} />
                    <span>VIP £150</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3B82F6' }} />
                    <span>Premium £100</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10B981' }} />
                    <span>Standard £50-60</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Mobile Selection Summary (sticky at bottom when seats selected) */}
            {selectedSeats.length > 0 && (
              <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-40">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{selectedSeats.length} seat{selectedSeats.length !== 1 ? 's' : ''} selected</p>
                    <p className="text-lg font-bold">£{totalPrice}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSelectedSeats([])}>
                      Clear
                    </Button>
                    <Button 
                      size="sm"
                      className="bg-gradient-to-r from-violet-600 to-indigo-600"
                      onClick={handleCheckout}
                    >
                      Checkout
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Booking Summary */}
          <div className="hidden lg:block lg:col-span-1">
            <Card className="p-6 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Your Selection
              </h3>
              
              {selectedSeats.length === 0 ? (
                <p className="text-gray-500 text-sm">Click on seats to select them</p>
              ) : (
                <div className="space-y-3">
                  {selectedSeats.map((seat) => (
                    <div key={seat.id} className="flex justify-between items-center text-sm">
                      <span className="font-medium">Seat {seat.id}</span>
                      <span className="text-gray-600">£{seat.price}</span>
                    </div>
                  ))}
                  
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center font-semibold">
                      <span>Total</span>
                      <span className="text-lg">£{totalPrice}</span>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 mt-4"
                    onClick={handleCheckout}
                  >
                    Checkout ({selectedSeats.length} seats)
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setSelectedSeats([])}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}

              {/* Price Legend */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Price Guide</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#8B5CF6' }} />
                      <span>VIP</span>
                    </div>
                    <span className="text-gray-600">£150</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3B82F6' }} />
                      <span>Premium</span>
                    </div>
                    <span className="text-gray-600">£100</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10B981' }} />
                      <span>Standard</span>
                    </div>
                    <span className="text-gray-600">£50-60</span>
                  </div>
                </div>
              </div>

              {/* Demo Notice */}
              <div className="mt-6 p-3 bg-violet-50 rounded-lg">
                <div className="flex gap-2">
                  <Info className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-violet-700">
                    <p className="font-medium">This is a demo</p>
                    <p>Sign up to create your own seating layouts!</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* CTA Card */}
            <Card className="p-6 mt-4 bg-gradient-to-br from-violet-600 to-indigo-600 text-white">
              <h3 className="font-semibold mb-2">Ready to get started?</h3>
              <p className="text-violet-200 text-sm mb-4">
                Create your own seating charts with our easy-to-use builder.
              </p>
              <Button 
                variant="secondary" 
                className="w-full bg-white text-violet-600 hover:bg-violet-50"
                onClick={() => navigate('/signup')}
              >
                Start Free Trial
              </Button>
            </Card>
          </div>
        </div>
      </div>

      {/* Mobile Cart Drawer */}
      {showMobileCart && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileCart(false)}
          />
          
          {/* Drawer */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b sticky top-0 bg-white flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Your Selection
              </h3>
              <button onClick={() => setShowMobileCart(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4">
              {selectedSeats.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No seats selected yet</p>
              ) : (
                <div className="space-y-3">
                  {selectedSeats.map((seat) => (
                    <div key={seat.id} className="flex justify-between items-center text-sm py-2 border-b">
                      <span className="font-medium">Seat {seat.id}</span>
                      <span className="text-gray-600">£{seat.price}</span>
                    </div>
                  ))}
                  
                  <div className="pt-3">
                    <div className="flex justify-between items-center font-semibold text-lg">
                      <span>Total</span>
                      <span>£{totalPrice}</span>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 mt-4"
                    onClick={() => {
                      handleCheckout();
                      setShowMobileCart(false);
                    }}
                  >
                    Checkout ({selectedSeats.length} seats)
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setSelectedSeats([])}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}

              {/* Price Legend */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Price Guide</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#8B5CF6' }} />
                      <span>VIP</span>
                    </div>
                    <span className="text-gray-600">£150</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3B82F6' }} />
                      <span>Premium</span>
                    </div>
                    <span className="text-gray-600">£100</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10B981' }} />
                      <span>Standard</span>
                    </div>
                    <span className="text-gray-600">£50-60</span>
                  </div>
                </div>
              </div>

              {/* Demo Notice */}
              <div className="mt-6 p-3 bg-violet-50 rounded-lg">
                <div className="flex gap-2">
                  <Info className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-violet-700">
                    <p className="font-medium">This is a demo</p>
                    <p>Sign up to create your own seating layouts!</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-4 p-4 bg-gradient-to-br from-violet-600 to-indigo-600 text-white rounded-lg">
                <h3 className="font-semibold mb-2">Ready to get started?</h3>
                <p className="text-violet-200 text-sm mb-3">
                  Create your own seating charts.
                </p>
                <Button 
                  variant="secondary" 
                  className="w-full bg-white text-violet-600 hover:bg-violet-50"
                  onClick={() => navigate('/signup')}
                >
                  Start Free Trial
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add padding at bottom for mobile sticky bar */}
      {selectedSeats.length > 0 && <div className="lg:hidden h-24" />}
    </div>
  );
}
